import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import Alert from '../common/Alert';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import {
  filterLabelClass,
  filterPanelClass,
  formInputClass,
  formLabelClass,
  formSelectClass,
} from '../common/uiClasses.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatLocationDropdownLabel, sortLocationsByNumber } from '../../utils/locationDropdownLabel.js';
import { pickedFile } from '../../utils/pickedFile.js';
import { useAuth } from '../../context/AuthContext.js';
import companySettlementService from '../../services/companySettlementService.js';
import { locationHubService } from '../../services/locationHubService.js';
import SelectField from '../common/SelectField.js';
import { PAYMENT_MODES } from '../paid-amount-settlement/paidAmountSettlementUi.js';

const dashboardBackTo = (role) => {
  if (role === 'Accountant') return '/accountant-dashboard';
  if (role === 'System Admin') return '/system-admin-dashboard';
  return '/admin-dashboard';
};

const money = (value) => fullCurrency(Number(value) || 0);

const localDateInput = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const agentStatus = (agent) => {
  if (!agent) return { label: '', className: '' };
  if (agent.pending) {
    return { label: 'Waiting for review', className: 'border-amber-200 bg-amber-50 text-amber-800' };
  }
  if (Number(agent.amount_due) > 0.009) {
    return { label: 'To collect', className: 'border-sky-200 bg-sky-50 text-sky-800' };
  }
  return { label: 'Settled', className: 'border-emerald-200 bg-emerald-50 text-emerald-800' };
};

const RecordAgentSettlementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const submitGuard = useRef(false);
  const [hubs, setHubs] = useState([]);
  const [hubId, setHubId] = useState('');
  const [hubLabel, setHubLabel] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [loadingHubs, setLoadingHubs] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(localDateInput);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [remarks, setRemarks] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [recordPreview, setRecordPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    locationHubService.getAllHubs(1, 500, { all: true, brief: 1 })
      .then((res) => {
        const data = res.data || {};
        setHubs(data.hubs || data.items || data.location_hubs || data.data || []);
      })
      .catch(() => setHubs([]))
      .finally(() => setLoadingHubs(false));
  }, []);

  const loadAgents = useCallback(async (branchId) => {
    if (!branchId) {
      setAgents([]);
      setHubLabel('');
      return;
    }
    try {
      setLoadingAgents(true);
      const res = await companySettlementService.listBranchAgents(branchId);
      const data = res?.data || {};
      const rows = (data.agents || []).filter((agent) => (
        Number(agent.paid_due) > 0.009
        || Number(agent.to_pay_due) > 0.009
        || Number(agent.amount_due) > 0.009
      ));
      setAgents(rows);
      setHubLabel(data.hub_label || '');
    } catch (err) {
      setAgents([]);
      Alert.error(err.response?.data?.error || 'Failed to load agents');
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  useEffect(() => {
    setAgentId('');
    setSelected(null);
    setAgents([]);
    setAmount('');
    setPaymentMode('cash');
    setPaymentRef('');
    setRemarks('');
    setReceipt(null);
    setRecordPreview(null);
    loadAgents(hubId);
  }, [hubId, loadAgents]);

  useEffect(() => {
    setRecordPreview(null);
    if (!selected || !hubId || selected.pending) return undefined;
    const received = Number(amount);
    if (!received || received <= 0) return undefined;
    if (received > Number(selected.amount_due) + 0.01) return undefined;
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      companySettlementService.recordPreview({
        location_hub_id: hubId,
        agent_user_id: selected.user_id,
        amount: String(received),
      })
        .then((res) => setRecordPreview(res?.data || null))
        .catch(() => setRecordPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [amount, hubId, selected]);

  const chooseAgent = (nextId) => {
    setAgentId(nextId);
    const agent = agents.find((row) => String(row.user_id) === String(nextId)) || null;
    setSelected(agent);
    setAmount(agent && Number(agent.amount_due) > 0 ? String(agent.amount_due) : '');
    setPaymentMode('cash');
    setPaymentRef('');
    setRemarks('');
    setReceipt(null);
  };

  const onFile = (event) => {
    const file = pickedFile(event);
    if (!file) {
      setReceipt(null);
      return;
    }
    const ok = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    if (file.type && !ok.includes(file.type)) {
      Alert.error('Payment image must be a PNG, JPG, WEBP, GIF, or PDF');
      event.target.value = '';
      return;
    }
    setReceipt(file);
  };

  const onSubmit = async () => {
    const received = Number(amount);
    const today = localDateInput();
    if (!selected || !hubId) return;
    if (!paymentDate) {
      Alert.error('Select the payment date');
      return;
    }
    if (paymentDate > today) {
      Alert.error('Payment date cannot be in the future');
      setPaymentDate(today);
      return;
    }
    if (!received || received <= 0) {
      Alert.error('Enter the amount received');
      return;
    }
    if (received > Number(selected.amount_due) + 0.01) {
      Alert.error('Amount is higher than what this agent still has to settle');
      return;
    }
    if (submitGuard.current) return;
    submitGuard.current = true;
    setSubmitting(true);
    const form = new FormData();
    form.append('location_hub_id', hubId);
    form.append('agent_user_id', selected.user_id);
    form.append('amount', String(received));
    form.append('payment_date', paymentDate);
    form.append('payment_mode', paymentMode);
    if (paymentRef.trim()) form.append('payment_ref', paymentRef.trim());
    if (remarks.trim()) form.append('remarks', remarks.trim());
    if (receipt) form.append('receipt', receipt);
    const paidDue = Number(selected.paid_due) || 0;
    const toPayDue = Number(selected.to_pay_due) || 0;
    const paidTake = Math.min(received, paidDue);
    const toPayTake = Math.max(0, Math.round((received - paidTake) * 100) / 100);
    companySettlementService.stageRecordedSettlement({
      batch_id: 'saving',
      agent_user_id: selected.user_id,
      agent_name: selected.name,
      hub_label: hubLabel,
      status: 'SAVING',
      paid_amount: Math.round(paidTake * 100) / 100,
      to_pay_amount: Math.round(Math.min(toPayTake, toPayDue) * 100) / 100,
      total_amount: received,
      submitted_at: new Date().toISOString(),
      reviewed_by_name: user?.name || '',
      reviewed_at: new Date().toISOString(),
      _pending: true,
    });
    const job = companySettlementService.recordReceived(form)
      .then((res) => {
        Alert.success(res?.message || 'Settlement recorded and approved');
      })
      .catch((err) => {
        companySettlementService.clearPendingSettlement();
        Alert.error(err.response?.data?.error || 'Could not record this settlement');
      });
    companySettlementService.trackSettlementJob(job);
    navigate('/company-settlement-approvals?tab=approved');
  };

  const todayValue = localDateInput();
  const due = Number(selected?.amount_due || 0);
  const canRecord = Boolean(selected) && !selected.pending && due > 0.009;
  const status = agentStatus(selected);
  const agentPlaceholder = !hubId
    ? 'Select a branch first'
    : (loadingAgents ? 'Loading agents…' : (agents.length ? 'Select agent' : 'No amount to collect'));

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Record agent settlement"
        subtitle="Record money already received from an agent. It is approved immediately."
        icon={Wallet}
        backTo={dashboardBackTo(user?.role)}
      />

      <div className={`${filterPanelClass} mb-4`}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-2 lg:items-end">
          <div className="min-w-0">
            <SelectField
              id="record-branch"
              variant="filter"
              portal
              searchable
              label="Branch"
              labelClassName={filterLabelClass}
              value={hubId}
              disabled={loadingHubs}
              placeholder={loadingHubs ? 'Loading branches…' : 'Select branch'}
              searchPlaceholder="Search branch or code"
              onChange={(event) => setHubId(event.target.value)}
              options={sortLocationsByNumber(hubs).map((hub) => {
                const label = formatLocationDropdownLabel(hub, true);
                return {
                  value: String(hub.id),
                  label,
                  searchText: [
                    label,
                    hub.lr_branch_code,
                    hub.location_code,
                    hub.hub_id,
                    hub.location,
                    hub.hub_name,
                  ].filter(Boolean).join(' '),
                };
              })}
            />
          </div>
          <div className="min-w-0">
            <SelectField
              id="record-agent"
              variant="filter"
              portal
              searchable
              label="Agent"
              labelClassName={filterLabelClass}
              value={agentId}
              disabled={!hubId || loadingAgents || !agents.length}
              placeholder={agentPlaceholder}
              searchPlaceholder="Search agent"
              onChange={(event) => chooseAgent(event.target.value)}
              options={agents.map((agent) => {
                const name = agent.name || agent.user_id;
                const dueAmount = Number(agent.amount_due) || 0;
                const status = agentStatus(agent);
                return {
                  value: String(agent.user_id),
                  triggerLabel: dueAmount > 0.009 ? `${name} · ${money(dueAmount)} to settle` : name,
                  searchText: [
                    name,
                    agent.user_id,
                    agent.paid_due,
                    agent.to_pay_due,
                    agent.amount_due,
                    status.label,
                  ].filter((part) => part !== undefined && part !== null && part !== '').join(' '),
                  label: (
                    <span className="flex w-full items-center gap-4">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-gray-900">{name}</span>
                        <span className="mt-0.5 block truncate text-xs font-normal text-gray-500">{agent.user_id}</span>
                      </span>
                      <span className="w-24 shrink-0 text-right">
                        <span className="block text-[11px] font-normal text-gray-500">Paid</span>
                        <span className="block tabular-nums text-sm text-gray-800">{money(agent.paid_due)}</span>
                      </span>
                      <span className="w-24 shrink-0 text-right">
                        <span className="block text-[11px] font-normal text-gray-500">To-Pay</span>
                        <span className="block tabular-nums text-sm text-gray-800">{money(agent.to_pay_due)}</span>
                      </span>
                      <span className="w-28 shrink-0 text-right">
                        <span className="block text-[11px] font-normal text-gray-500">To settle</span>
                        <span className="block tabular-nums text-sm font-semibold text-gray-900">{money(dueAmount)}</span>
                      </span>
                      <span className={`inline-flex w-[8.5rem] shrink-0 justify-center rounded-full border px-2 py-0.5 text-xs font-medium ${status.className}`}>
                        {status.label}
                      </span>
                    </span>
                  ),
                };
              })}
            />
          </div>
        </div>
      </div>

      {!hubId ? (
        <p className="rounded-xl border border-gray-100 bg-white py-14 text-center text-sm text-gray-400">
          Select a branch, then choose an agent
        </p>
      ) : loadingAgents ? (
        <div className="rounded-xl border border-gray-100 bg-white">
          <Loader fullScreen={false} message="Loading agents..." />
        </div>
      ) : !selected ? (
        <p className="rounded-xl border border-gray-100 bg-white py-14 text-center text-sm text-gray-400">
          {agents.length ? 'Select an agent to see the amount to collect' : 'No amount to collect from agents on this branch'}
        </p>
      ) : (
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:px-5">
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-gray-900">{selected.name || selected.user_id}</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {selected.user_id}
                {hubLabel ? ` · ${hubLabel}` : ''}
              </p>
            </div>
            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${status.className}`}>
              {status.label}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3 sm:p-5">
            <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-4 py-3">
              <p className="text-xs text-gray-500">Paid</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-sky-900">{money(selected.paid_due)}</p>
              <p className="mt-0.5 text-[11px] text-gray-500">Paid freight still with the agent</p>
            </div>
            <div className="rounded-xl border border-orange-100 bg-orange-50/60 px-4 py-3">
              <p className="text-xs text-gray-500">To-Pay</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-orange-900">{money(selected.to_pay_due)}</p>
              <p className="mt-0.5 text-[11px] text-gray-500">To-pay dues still to collect</p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3">
              <p className="text-xs text-gray-500">To settle</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-amber-900">{money(due)}</p>
              <p className="mt-0.5 text-[11px] text-gray-500">Amount still to collect from this agent</p>
            </div>
          </div>

          {selected.pending ? (
            <div className="border-t border-gray-100 px-4 py-4 sm:px-5">
              <p className="text-sm text-amber-800">
                A settlement for this agent is already waiting for review
                {selected.pending_request_id ? ` (${selected.pending_request_id})` : ''}.
                Approve or reject that one before recording another.
              </p>
            </div>
          ) : null}

          {!selected.pending && due <= 0.009 ? (
            <div className="border-t border-gray-100 px-4 py-4 sm:px-5">
              <p className="text-sm text-emerald-800">This agent has nothing left to settle.</p>
            </div>
          ) : null}

          {canRecord ? (
            <div className="space-y-4 border-t border-gray-100 px-4 py-4 sm:px-5">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Record received amount</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Recording approves it immediately and returns to the approvals list. The settlement is saved on this date at 11:59 PM.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className={formLabelClass}>Payment date</span>
                  <input
                    type="date"
                    className={formInputClass()}
                    value={paymentDate}
                    max={todayValue}
                    onChange={(event) => {
                      const next = event.target.value;
                      if (next && next > todayValue) {
                        Alert.error('Payment date cannot be in the future');
                        setPaymentDate(todayValue);
                        return;
                      }
                      setPaymentDate(next);
                    }}
                  />
                  <span className="mt-1 block text-[11px] text-gray-500">
                    Today is already selected. Pick an earlier day if the agent paid then. Future dates stay locked.
                  </span>
                </label>
                <label className="block">
                  <span className={formLabelClass}>Amount received</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={formInputClass()}
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className={formLabelClass}>Payment mode</span>
                  <select
                    className={formSelectClass()}
                    value={paymentMode}
                    onChange={(event) => setPaymentMode(event.target.value)}
                  >
                    {PAYMENT_MODES.map((mode) => (
                      <option key={mode.value} value={mode.value}>{mode.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className={formLabelClass}>Reference</span>
                  <input
                    className={formInputClass()}
                    value={paymentRef}
                    placeholder="UTR or receipt no."
                    onChange={(event) => setPaymentRef(event.target.value)}
                  />
                </label>
              </div>
              <label className="block">
                <span className={formLabelClass}>Remarks</span>
                <input
                  className={formInputClass()}
                  value={remarks}
                  placeholder="Optional note"
                  onChange={(event) => setRemarks(event.target.value)}
                />
              </label>
              <label className="block max-w-lg">
                <span className={formLabelClass}>Payment image</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                  className="block w-full rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700"
                  onChange={onFile}
                />
                <span className="mt-1 block text-[11px] text-gray-500">Optional. PNG, JPG, WEBP, GIF, or PDF.</span>
              </label>

              {(previewLoading || recordPreview) ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3">
                  <p className="text-xs font-medium text-gray-600">Included in this settlement</p>
                  {previewLoading ? (
                    <p className="mt-2 text-sm text-gray-500">Calculating lines…</p>
                  ) : (
                    <>
                      {recordPreview.destination_label ? (
                        <p className="mt-1 text-xs text-gray-500">
                          Posts to {recordPreview.destination_label}
                        </p>
                      ) : null}
                      <ul className="mt-2 space-y-1.5">
                        {(recordPreview.lines || []).map((line) => (
                          <li
                            key={`${line.kind}-${line.ref}-${line.amount}`}
                            className="flex justify-between gap-3 text-sm tabular-nums"
                          >
                            <span>
                              <span className="text-[11px] uppercase text-gray-500 mr-2">
                                {line.kind === 'to_pay' ? 'Delivery' : 'Booking'}
                              </span>
                              <span className="font-mono text-xs text-gray-800">{line.label}</span>
                            </span>
                            <span className="font-medium text-gray-900">{money(line.amount)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 flex flex-wrap gap-4 border-t border-gray-200 pt-2 text-sm tabular-nums">
                        <span>Paid {money(recordPreview.paid_amount)}</span>
                        <span>To-Pay {money(recordPreview.to_pay_amount)}</span>
                        <span className="font-semibold">Total {money(recordPreview.total_amount)}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : null}

              <div className="flex justify-end">
                <Button onClick={onSubmit} disabled={submitting} loading={submitting}>
                  Approve settlement
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </DashboardLayout>
  );
};

export default RecordAgentSettlementPage;
